import React, { useMemo, useState } from "react"
import Chart from "react-apexcharts"
import { Modal, ModalBody, ModalHeader, Spinner } from "reactstrap"
import { FetchPostData } from "utils/Common_api_functions"

const fmtGBP = val => {
  const n = Number(val)
  const abs = Math.abs(n)
  const prefix = n < 0 ? "-£" : "£"
  if (abs >= 1000000) return `${prefix}${(abs / 1000000).toFixed(1)}M`
  if (abs >= 1000) return `${prefix}${Math.round(abs / 1000)}k`
  return `${prefix}${Math.round(abs)}`
}

const CombinedContributionBar = ({
  data = {},
  title = "Contribution",
  dateRange,
  selectedMonths,
  selectedYears,
}) => {
  const [modal, setModal] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [drillData, setDrillData] = useState([])
  const [drillLoading, setDrillLoading] = useState(false)

  const {
    customerList,
    categoryList,
    yLabels,
    customerSeries,
    categorySeries,
  } = useMemo(() => {
    const allCustomers = [
      ...(data?.positiveCustomerContribution || []).map(item => ({
        name: item.customerName,
        code: item.customerCode,
        value: Number(item.totalNetMarginContribution),
      })),
      ...(data?.negetiveCustomerContribution || []).map(item => ({
        name: item.customerName,
        code: item.customerCode,
        value: Number(item.totalNetMarginContribution),
      })),
    ].sort((a, b) => b.value - a.value)

    const allCategories = [
      ...(data?.positiveCategoryContribution || []).map(item => ({
        name: item.categoryName,
        value: Number(item.totalNetMarginContribution),
      })),
      ...(data?.negetiveCategoryContribution || []).map(item => ({
        name: item.categoryName,
        value: Number(item.totalNetMarginContribution),
      })),
    ].sort((a, b) => b.value - a.value)

    const len = Math.max(allCustomers.length, allCategories.length)
    const labels = Array.from({ length: len }, (_, i) => {
      const c = allCustomers[i]?.name ?? ""
      const cat = allCategories[i]?.name ?? ""
      return [c, cat].filter(Boolean)
    })

    return {
      customerList: allCustomers,
      categoryList: allCategories,
      yLabels: labels,
      customerSeries: labels.map((_, i) => allCustomers[i]?.value ?? null),
      categorySeries: labels.map((_, i) => allCategories[i]?.value ?? null),
    }
  }, [data])

  const handleBarClick = async (_event, _ctx, config) => {
    if (config.seriesIndex !== 0) return // only Customer series
    const idx = config.dataPointIndex
    if (idx < 0 || idx === undefined) return
    const customer = customerList[idx]
    if (!customer) return

    console.log(customer)

    setSelectedCustomer(customer)
    setModal(true)
    setDrillData([])
    setDrillLoading(true)

    const res = await FetchPostData(setDrillLoading, "sheet/customer/data", {
      customerCode: customer.code,
      ...(dateRange?.[0]?.isValid()
        ? { startDate: dateRange[0].format("YYYY-MM-DD") }
        : {}),
      ...(dateRange?.[1]?.isValid()
        ? { endDate: dateRange[1].format("YYYY-MM-DD") }
        : {}),
      ...(selectedMonths?.length
        ? { month: selectedMonths.map(m => m.value).join(",") }
        : {}),
      ...(selectedYears?.length
        ? { year: selectedYears.map(y => y.value).join(",") }
        : {}),
    })

    setDrillData(res?.data?.customerData || [])
    setDrillLoading(false)
  }

  const options = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      background: "transparent",
      events: { dataPointSelection: handleBarClick },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        barHeight: "50%",
        grouped: true,
      },
    },
    colors: [
      ({ value }) => (value >= 0 ? "#22C55E" : "#EF5350"),
      ({ value }) => (value >= 0 ? "#3B82F6" : "#F59E0B"),
    ],
    dataLabels: {
      enabled: true,
      formatter: val => (val !== null ? fmtGBP(val) : ""),
      style: { fontSize: "10px", fontWeight: 700, colors: ["#1e293b"] },
    },
    xaxis: {
      categories: yLabels,
      labels: {
        formatter: val => fmtGBP(Number(val)),
        style: { fontSize: "11px" },
      },
    },
    yaxis: {
      title: {
        text: "Customer & Products",
        offsetX: 10,
        style: { fontSize: "12px", fontWeight: 700, color: "#374151" },
      },
      labels: {
        maxWidth: 220,
        offsetX: 10,
        style: { fontSize: "11px", fontWeight: 400, colors: "#374151" },
      },
    },
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 4,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
    },
    tooltip: {
      shared: true,
      intersect: false,
      custom: ({ dataPointIndex }) => {
        const c = customerList[dataPointIndex]
        const cat = categoryList[dataPointIndex]
        const row = (color, label, name, val) =>
          name
            ? `<div style="display:flex;align-items:center;gap:6px;padding:3px 0">
                <span style="width:10px;height:10px;border-radius:2px;background:${color};display:inline-block;flex-shrink:0"></span>
                <span style="font-size:12px;color:#fff"><b>${label}:</b> ${name}</span>
                <span style="font-size:12px;font-weight:700;color:${
                  Number(val) >= 0 ? "#86efac" : "#fca5a5"
                };margin-left:auto;padding-left:12px">${fmtGBP(val)}</span>
               </div>`
            : ""
        const hint = c
          ? '<div style="font-size:10px;color:#94a3b8;margin-top:4px">Click customer bar to view product breakdown</div>'
          : ""
        return `<div style="background:#222;border:1px solid #e2e8f0;border-radius:8px;padding:10px 14px;min-width:220px">
          <div style="font-size:11px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px">Position ${
            dataPointIndex + 1
          }</div>
          ${row("#4361ee", "Customer", c?.name, c?.value)}
          ${row("#F59E0B", "Category", cat?.name, cat?.value)}
          ${hint}
        </div>`
      },
    },
    legend: {
      show: true,
      position: "top",
      horizontalAlign: "right",
      fontSize: "12px",
      fontWeight: 600,
      markers: { radius: 3 },
    },
  }

  const chartHeight = Math.max(380, yLabels.length * 52)

  return (
    <>
      <div
        style={{
          background: "#fff",
          borderRadius: "16px",
          padding: "20px 16px 40px",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          border: "1px solid #e2e8f0",
          height: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "4px",
          }}
        >
          <div>
            <h4
              style={{
                fontSize: "15px",
                fontWeight: 700,
                margin: 0,
                color: "#1a1a2e",
              }}
            >
              {title}
            </h4>
            <p
              style={{ margin: "2px 0 0", fontSize: "12px", color: "#64748b" }}
            >
              Click a customer bar to view product breakdown
            </p>
          </div>
        </div>

        <Chart
          options={options}
          series={[
            { name: "Customer", data: customerSeries },
            { name: "Category", data: categorySeries },
          ]}
          type="bar"
          height={chartHeight}
        />
        <div
          style={{
            textAlign: "center",
            fontSize: "12px",
            fontWeight: 700,
            color: "#374151",
            marginTop: "0px",
          }}
        >
          Net Margin Contribution (£)
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} size="lg" centered>
        <ModalHeader
          toggle={() => setModal(false)}
          style={{ borderBottom: "1px solid #f1f5f9", padding: "14px 20px" }}
        >
          <span style={{ fontWeight: 700, fontSize: "15px", color: "#fff" }}>
            {selectedCustomer?.name} — Product Breakdown
          </span>
        </ModalHeader>
        <ModalBody style={{ padding: 0 }}>
          {drillLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "48px",
              }}
            >
              <Spinner color="primary" />
            </div>
          ) : drillData.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "48px",
                color: "#94a3b8",
                fontSize: "14px",
              }}
            >
              No data available
            </div>
          ) : (
            (() => {
              const grouped = drillData.reduce((acc, row) => {
                const key = row.productCode
                if (!acc[key]) {
                  acc[key] = {
                    productName: row.productName,
                    netMarginSum: 0,
                    netMarginContribution: 0,
                    count: 0,
                  }
                }
                acc[key].netMarginSum += Number(row.netMarginPercent)
                acc[key].netMarginContribution += Number(
                  row.netMarginContribution
                )
                acc[key].count += 1
                return acc
              }, {})
              const rows = Object.values(grouped)
                .map(r => ({ ...r, netMargin: r.netMarginSum }))
                .sort(
                  (a, b) => b.netMarginContribution - a.netMarginContribution
                )
              return (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "13px",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          background: "#f8fafc",
                          borderBottom: "2px solid #e2e8f0",
                        }}
                      >
                        {[
                          { label: "Product Name", align: "left" },
                          { label: "Net Margin %", align: "right" },
                          { label: "Net Margin Contribution", align: "right" },
                        ].map((h, i) => (
                          <th
                            key={i}
                            style={{
                              padding: "10px 16px",
                              textAlign: h.align,
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#64748b",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => {
                        const color =
                          row.netMarginContribution >= 0 ? "#15803d" : "#be123c"
                        return (
                          <tr
                            key={i}
                            style={{
                              borderBottom: "1px solid #f1f5f9",
                              background: i % 2 === 0 ? "#fff" : "#fafbfc",
                            }}
                          >
                            <td
                              style={{
                                padding: "10px 16px",
                                color: "#1e293b",
                                fontWeight: 500,
                              }}
                            >
                              {row.productName}
                            </td>
                            <td
                              style={{
                                padding: "10px 16px",
                                textAlign: "right",
                                fontWeight: 700,
                                color,
                              }}
                            >
                              {Number(row.netMargin).toFixed(2)}%
                            </td>
                            <td
                              style={{
                                padding: "10px 16px",
                                textAlign: "right",
                                fontWeight: 700,
                                color,
                              }}
                            >
                              {fmtGBP(row.netMarginContribution)}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )
            })()
          )}
        </ModalBody>
      </Modal>
    </>
  )
}

export default CombinedContributionBar
